import { matchPath } from "react-router-dom";
import type {
  RoomCaptureRoutePattern,
  RoomCaptureSection,
} from "../../../../../api/src/shared/domain/room";

/**
 * Every room route pattern lives here as the single source of truth.
 * `satisfies` keeps the shape honest at compile time: any entry that isn't a
 * `/room/.../:roomId[...]` path fails to type-check — you cannot add a
 * pattern that doesn't actually carry `:roomId`.
 *
 * Kept in sync with the `matchRoute(...)` calls in `AppRoutes.tsx`. Adding a
 * new room route? Add an entry here too (or this function returns null for it).
 */
type RoomRoutePattern =
  `/room/${string}/:roomId` | `/room/${string}/:roomId/${string}`;

export const ROOM_ROUTES = {
  discovery: "/room/discovery/:roomId",
  conferences: "/room/conferences/:roomId",
  resources: "/room/resources/:roomId",
  congressTopSessions: "/room/congress/:roomId/:spaceId/top-sessions",
  congressPdf: "/room/congress/:roomId/:spaceId/pdf/:cardId",
  congressSpace: "/room/congress/:roomId/:spaceId",
  playlists: "/room/playlists/:roomId",

  article: "/room/post/:roomId/:slug",
  articleChat: "/room/post/:roomId/:slug/chat",
  articleChatConversation: "/room/post/:roomId/:slug/chat/conversation/:id",

  videoStory: "/room/video/story/:roomId/:slug",
  video: "/room/video/:roomId/:slug",
  infographic: "/room/infographic/:roomId/:slug",
  playlist: "/room/playlist/:roomId/:id",
  search: "/room/search/:roomId",
  gptChatNew: "/room/gpt/chat/new/:roomId",
  gptChatConversation: "/room/gpt/chat/conversation/:roomId/:id",
  termsPrivacy: "/room/terms-privacy/:roomId",
  quiz: "/room/quiz/:roomId",
  quizSlides: "/room/quiz/:roomId/:quizId",
  podcast: "/room/podcast/:roomId/:podcastId",
} as const satisfies Record<string, RoomRoutePattern>;

/**
 * Every capturable room section must be a route here, shaped exactly as the
 * screenshot pipeline builds it (`roomCapturePath`, apps/api shared/domain/room).
 *
 * The direction is ONE-WAY on purpose. This asserts `ROOM_ROUTES` is assignable
 * to the capture shape, and assignability tolerates extra properties — so adding
 * a room route above never forces it into `ROOM_CAPTURE_SECTIONS`. The reverse
 * does not hold: add a capture section with no route here, or reshape one of
 * these two patterns (to `/rooms/…`, or `/room/:roomId/:section`), and this stops
 * compiling. That is the intended signal to move `roomCapturePath` with it —
 * without it the pipeline would keep requesting a url this app no longer serves
 * and return a 404 page as a screenshot.
 */
type AssertCaptureSectionsAreRoutes<
  T extends { [S in RoomCaptureSection]: RoomCaptureRoutePattern<S> },
> = T;
// Exported so the alias is not dead code, NOT as public API: nothing imports it
// and nothing should. The assertion is the whole purpose; the name is a handle.
export type CaptureRoutesArePinned = AssertCaptureSectionsAreRoutes<
  typeof ROOM_ROUTES
>;

/**
 * Extract roomId from URL pathname using React Router's matchPath utility.
 * For use outside React components (analytics, etc.).
 */
export function extractRoomIdFromPath(pathname: string): string | null {
  for (const pattern of Object.values(ROOM_ROUTES)) {
    const match = matchPath<{ roomId: string }>(pathname, {
      path: pattern,
      exact: false,
    });
    if (match?.params.roomId !== undefined) {
      return match.params.roomId;
    }
  }
  return null;
}
